import { toLooseKey } from '@attain-apps/string-utils'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as common from '../../common'
import { CashDraw } from '../cash-draw'
import type { DocumentNumber } from '../document-number'
import { ExpenseCategory } from '../expense-category'
import type * as payment from '../payment'
import type { ProjectId } from '../project-id'
import { normalizeDate, normalizeText, parseNumber } from '../sheet-cells'
import type { Subaccount } from '../subaccount'

import { ExpenseReports } from './expense-reports'

/**
 * Normalized JavaScript representation of the BCH Defend to Spend workbooks.
 *
 * This type covers the parsing/normalization part of the
 * `BCH Defend to Spend Outline`: convert the source spreadsheets into stable
 * JavaScript rows that can be joined by project, document, and payment
 * identifiers instead of by direct Excel cell references.
 *
 * Source files:
 * - the cash draw workbook, read as {@link CashDraw} describes it
 * - `CHGLN53A_9.25.2026.xlsm` and the two before it: the Current Mid-Month,
 *   Prior Month End, and Prior Mid-Month reports (see {@link ExpenseReports}),
 *   each a wide project-by-expense-category amount sheet
 * - `subaccount_summary_-_April 2026.csv`: award metadata keyed by
 *   `DOCUMENT NUMBER`, including `SUBACCOUNT` and `AWARD END DATE`
 *
 * Normalized rows preserve the outline's join points:
 * - project references: `ProjectID` / `All Projects`
 * - document references: `Document #`, `Subacct Code`, and `DOCUMENT NUMBER`
 * - payment references: `Payment Type`, `Subacct Code`, and PMS Upload row
 *   number
 * - amount references: expense category columns and `Subacct Amt Requested $`
 *
 * This is the input shape for the outline's balance formula:
 *
 * ```ts
 * currentMonthBalance =
 *   priorMonthEnd - priorMidMonth + currentMidMonth
 * ```
 */
export interface WorkbookData {
  readonly cashDraw: CashDraw
  readonly justifications: WorkbookData.Justifications
  readonly subaccountSummary: readonly WorkbookData.SubaccountSummaryRow[]
}
export namespace WorkbookData {
  /**
   * Parsed workbook sections before they are joined into one BCH workbook data
   * aggregate.
   */
  export interface Parts {
    readonly cashDraw: CashDraw
    readonly justifications: Justifications
    readonly subaccountSummary: readonly SubaccountSummaryRow[]
  }
  export namespace Parts {
    export const isParts: TypeChecker<Parts> = TypeChecker.Factory.lazy(() =>
      TypeChecker.Factory.object({
        cashDraw: CashDraw.isCashDraw,
        justifications: Justifications.isJustifications,
        subaccountSummary: TypeChecker.Factory.list(
          SubaccountSummaryRow.isSubaccountSummaryRow,
        ),
      }).usingAlias('WorkbookData.Parts'),
    )
  }

  /**
   * BCH source worksheets needed to build normalized workbook data.
   */
  export interface SourceSheets {
    readonly cashDraw: CashDraw.Worksheets
    readonly justifications: Justifications.Worksheets
    readonly subaccountSummary: readonly common.SheetRow[]
  }
  export namespace SourceSheets {
    export const isSourceSheets: TypeChecker<SourceSheets> =
      TypeChecker.Factory.lazy(() =>
        TypeChecker.Factory.object({
          cashDraw: CashDraw.Worksheets.isWorksheets,
          justifications: Justifications.Worksheets.isWorksheets,
          subaccountSummary: TypeChecker.Factory.list(
            common.SheetRow.isSheetRow,
          ),
        }).usingAlias('WorkbookData.SourceSheets'),
      )
    export const assertIsSourceSheets: TypeAsserter<SourceSheets> =
      TypeAsserter.Factory.from(isSourceSheets)
  }

  export const isWorkbookData: TypeChecker<WorkbookData> =
    TypeChecker.Factory.lazy(() =>
      TypeChecker.Factory.object({
        cashDraw: CashDraw.isCashDraw,
        justifications: Justifications.isJustifications,
        subaccountSummary: TypeChecker.Factory.list(
          SubaccountSummaryRow.isSubaccountSummaryRow,
        ),
      }),
    )

  /**
   * Builds a BCH workbook data aggregate from already-normalized workbook
   * sections.
   */
  export function from(parts: Readonly<Parts>): WorkbookData {
    return {
      cashDraw: {
        pacct: [...parts.cashDraw.pacct],
        pmsDownload: [...parts.cashDraw.pmsDownload],
        pmsUpload: [...parts.cashDraw.pmsUpload],
      },
      justifications: {
        currentMidMonth: [...parts.justifications.currentMidMonth],
        priorMidMonth: [...parts.justifications.priorMidMonth],
        priorMonthEnd: [...parts.justifications.priorMonthEnd],
      },
      subaccountSummary: [...parts.subaccountSummary],
    }
  }

  /**
   * Builds normalized BCH workbook data from worksheet rows.
   */
  export function fromSheets(sheets: Readonly<SourceSheets>): WorkbookData {
    SourceSheets.assertIsSourceSheets(sheets)
    return from({
      cashDraw: CashDraw.fromWorksheets(sheets.cashDraw),
      justifications: Justifications.fromWorksheets(sheets.justifications),
      subaccountSummary: SubaccountSummaryRow.fromSheet(
        sheets.subaccountSummary,
      ),
    })
  }

  export interface Justifications {
    readonly currentMidMonth: readonly ExpenseRow[]
    readonly priorMonthEnd: readonly ExpenseRow[]
    readonly priorMidMonth: readonly ExpenseRow[]
  }
  export namespace Justifications {
    /** The rows of each CHGLN53A report's sheet. */
    export type Worksheets = ExpenseReports<readonly common.SheetRow[]>
    export namespace Worksheets {
      export const isWorksheets: TypeChecker<Worksheets> =
        TypeChecker.Factory.object({
          currentMidMonth: TypeChecker.Factory.list(common.SheetRow.isSheetRow),
          priorMidMonth: TypeChecker.Factory.list(common.SheetRow.isSheetRow),
          priorMonthEnd: TypeChecker.Factory.list(common.SheetRow.isSheetRow),
        }).usingAlias('WorkbookData.Justifications.Worksheets')
    }

    export const isJustifications: TypeChecker<Justifications> =
      TypeChecker.Factory.lazy(() =>
        TypeChecker.Factory.object({
          currentMidMonth: TypeChecker.Factory.list(ExpenseRow.isExpenseRow),
          priorMidMonth: TypeChecker.Factory.list(ExpenseRow.isExpenseRow),
          priorMonthEnd: TypeChecker.Factory.list(ExpenseRow.isExpenseRow),
        }),
      )

    /**
     * Builds the normalized justifications section from the CHGLN53A reports,
     * after checking each report's As of Date fits its part.
     */
    export function fromWorksheets(
      sheets: Readonly<Worksheets>,
    ): Justifications {
      ExpenseReports.assertAsOfDates(sheets)
      return {
        currentMidMonth: ExpenseRow.fromSheet(sheets.currentMidMonth),
        priorMidMonth: ExpenseRow.fromSheet(sheets.priorMidMonth),
        priorMonthEnd: ExpenseRow.fromSheet(sheets.priorMonthEnd),
      }
    }
  }

  export interface ExpenseRow {
    readonly projectId: ProjectId
    readonly expenseCategory: ExpenseCategory
    readonly amount: number
    readonly raw?: Record<string, unknown>
  }
  export namespace ExpenseRow {
    export const isExpenseRow: TypeChecker<ExpenseRow> =
      TypeChecker.Factory.object({
        amount: TypeChecker.isNumber,
        expenseCategory: TypeChecker.isString,
        projectId: TypeChecker.isString,
        raw: TypeChecker.Factory.optional(common.SheetRow.isRecord),
      })

    /**
     * Parses a CHGLN53A report into project/category balances.
     *
     * The report is wide: each column after the project is an expense
     * category that becomes its own normalized row. As downloaded from
     * Envision, two things need care:
     * - Some category headers are split over two rows (`Consultant` above
     *   `Services`), so the row above the header is read with it when it is
     *   that upper half.
     * - Project IDs sit one column to the right of the `All Projects`
     *   header, so the project column is found from the data.
     */
    export function fromSheet(rows: readonly common.SheetRow[]): ExpenseRow[] {
      const { body, columns, header, preamble } = common.SheetRow.parseTable(
        rows,
        { projectId: ['All Projects'] },
      )
      const headerAbove = _splitHeaderAbove(preamble, columns.projectId)
      const categoryColumns = header.flatMap((cell, column) => {
        const label = normalizeText(
          [headerAbove[column], cell]
            .map(normalizeText)
            .filter(Boolean)
            .join(' '),
        )
        return label && column > columns.projectId
          ? [{ category: ExpenseCategory.fromHeader(label), column }]
          : []
      })
      const projectColumn = _projectColumn(
        body,
        columns.projectId,
        categoryColumns[0]?.column,
      )

      return body.flatMap(row => {
        const projectId = normalizeText(row[projectColumn])
        if (!projectId || toLooseKey(projectId) === 'total') {
          return []
        }

        const raw = common.SheetRow.toRecord(header, row)
        return categoryColumns.map(({ category, column }) => ({
          amount: parseNumber(row[column]),
          expenseCategory: category,
          projectId,
          raw,
        }))
      })
    }
  }

  export interface SubaccountSummaryRow {
    readonly documentNumber: DocumentNumber
    readonly subaccount?: Subaccount
    readonly awardEndDate?: string
    readonly raw?: Record<string, unknown>
  }
  export namespace SubaccountSummaryRow {
    export const isSubaccountSummaryRow: TypeChecker<SubaccountSummaryRow> =
      TypeChecker.Factory.object({
        awardEndDate: TypeChecker.Factory.optional(TypeChecker.isString),
        documentNumber: TypeChecker.isString,
        raw: TypeChecker.Factory.optional(common.SheetRow.isRecord),
        subaccount: TypeChecker.Factory.optional(TypeChecker.isString),
      })

    /**
     * Parses the BCH subaccount summary export.
     */
    export function fromSheet(
      rows: readonly common.SheetRow[],
    ): SubaccountSummaryRow[] {
      const { body, columns, header } = common.SheetRow.parseTable(rows, {
        documentNumber: ['DOCUMENT NUMBER'],
      })
      const subaccountCol = common.SheetRow.findOptionalColumn(header, [
        'SUBACCOUNT',
      ])
      const awardEndDateCol = common.SheetRow.findOptionalColumn(header, [
        'AWARD END DATE',
      ])

      return body
        .map(row => {
          const documentNumber = normalizeText(row[columns.documentNumber])
          if (!documentNumber) {
            return undefined
          }
          return {
            awardEndDate: common.SheetRow.valueAt(
              row,
              awardEndDateCol,
              normalizeDate,
            ),
            documentNumber,
            raw: common.SheetRow.toRecord(header, row),
            subaccount: common.SheetRow.valueAt(
              row,
              subaccountCol,
              normalizeText,
            ),
          } satisfies SubaccountSummaryRow
        })
        .filter(TypeChecker.isNotNullish.raw)
    }
  }

  /**
   * The row above the header when it is the upper half of split category
   * headers, else an empty row. The upper half is empty up to the `All
   * Projects` column; a title or run-date row is not, and joining its cells
   * would turn them into category names.
   */
  function _splitHeaderAbove(
    preamble: readonly common.SheetRow[],
    projectColumn: number,
  ): common.SheetRow {
    const above = preamble.at(-1) ?? []
    const hasLeadingText = above
      .slice(0, projectColumn + 1)
      .some(cell => normalizeText(cell) !== undefined)
    return hasLeadingText ? [] : above
  }

  /**
   * The column holding project IDs: of the `All Projects` header's own column
   * and the columns between it and the first category, the one with the most
   * values. Counting rather than taking the first non-empty column matters
   * because the `Totals` label sits in the header's column when the IDs are
   * shifted.
   */
  function _projectColumn(
    body: readonly common.SheetRow[],
    headerColumn: number,
    firstCategoryColumn: number | undefined,
  ): number {
    const end = firstCategoryColumn ?? headerColumn + 1
    let best = { column: headerColumn, count: -1 }
    for (let column = headerColumn; column < end; column += 1) {
      const count = body.filter(
        row => normalizeText(row[column]) !== undefined,
      ).length
      if (count > best.count) {
        best = { column, count }
      }
    }
    return best.column
  }
}
